import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";

import { fieldAnchorId } from "@/lib/fieldPath";

/**
 * The registry that lets a validation finding reach the control it is about.
 *
 * A control registers itself by path; a finding resolves its `path` against the
 * registry by **exact match**. Two consequences, both required by #7:
 *
 *  - A finding whose path is absent or unregistered gets no jump affordance.
 *    A link to a control that is not on screen is a dead control, which the
 *    shell's invariants forbid outright.
 *  - The finding never needs to know how ids are generated, and the control
 *    never needs to know a finding exists.
 *
 * This is the seam #33–#36 consume. An asset editor calls `useFieldAnchor` on
 * each control it renders and inherits both the jump target and the id used for
 * `aria-describedby`; nothing else about diagnostics is its concern.
 */

interface FieldAnchorRegistry {
  register: (path: string, element: HTMLElement | null) => void;
  /** Paths currently rendered. Identity changes when the set changes. */
  paths: ReadonlySet<string>;
  focus: (path: string) => boolean;
}

const FieldAnchorContext = createContext<FieldAnchorRegistry | null>(null);

export function FieldAnchorProvider({ children }: { children: ReactNode }) {
  const elements = useRef(new Map<string, HTMLElement>());
  const [paths, setPaths] = useState<ReadonlySet<string>>(new Set());

  const register = useCallback((path: string, element: HTMLElement | null) => {
    if (element) {
      elements.current.set(path, element);
    } else {
      elements.current.delete(path);
    }
    // A plain Set copy rather than a mutation, so consumers re-render when a
    // control mounts or unmounts and a finding's affordance appears or
    // disappears with it.
    setPaths(new Set(elements.current.keys()));
  }, []);

  const focus = useCallback((path: string) => {
    const element = elements.current.get(path);
    if (!element) {
      return false;
    }
    element.scrollIntoView({ block: "center" });
    element.focus({ preventScroll: true });
    return true;
  }, []);

  const value = useMemo<FieldAnchorRegistry>(
    () => ({ register, paths, focus }),
    [register, paths, focus],
  );

  return (
    <FieldAnchorContext.Provider value={value}>
      {children}
    </FieldAnchorContext.Provider>
  );
}

/**
 * Read the registry.
 *
 * Returns an inert registry outside a provider rather than throwing: a
 * diagnostics list is legitimately rendered on surfaces that have no fields —
 * the project Overview, for instance — and there it correctly resolves nothing.
 */
const EMPTY_REGISTRY: FieldAnchorRegistry = {
  register: () => undefined,
  paths: new Set(),
  focus: () => false,
};

export function useFieldAnchorRegistry(): FieldAnchorRegistry {
  return useContext(FieldAnchorContext) ?? EMPTY_REGISTRY;
}

/**
 * Register one control as the anchor for a field path.
 *
 * Returns the ref to attach and the DOM id the control must carry, so that a
 * finding can also point `aria-describedby` at it.
 */
export function useFieldAnchor(path: string): {
  ref: (element: HTMLElement | null) => void;
  id: string;
} {
  const { register } = useFieldAnchorRegistry();
  const ref = useCallback(
    (element: HTMLElement | null) => register(path, element),
    [register, path],
  );
  return { ref, id: fieldAnchorId(path) };
}
